import {
	SANDBOX_PROVIDER_KEY_PREFIX,
	SANDBOX_ROW_KEY_PREFIX,
} from "@lobu/core/reserved";
import type {
	ConnectionFacets,
	ConnectorDefinitionItem,
} from "@/lib/api/connections";
import type { SandboxItem } from "@/lib/api/sandboxes";

/**
 * Remote sandboxes as connectors-list / catalog rows.
 *
 * Same shape as model-provider rows (`inference-rows.ts`): a sandbox is a
 * credential-backed thing you connect, so it belongs under Connectors. It is
 * NOT a `connections` row — sandboxes live in their own table. Read-layer
 * union only.
 *
 * Two key shapes:
 *  - `sandbox:{id}` — an org's sandbox instance (list + detail)
 *  - `sandbox-provider:{kind}` — a provider kind in the catalog (e.g. vercel).
 *    Picking it opens the create form locked to that kind — no second
 *    "which provider?" step.
 */

export { SANDBOX_PROVIDER_KEY_PREFIX, SANDBOX_ROW_KEY_PREFIX };

const RUNTIME_ONLY_FACETS: ConnectionFacets = {
	data: false,
	chat: false,
	actions: false,
	audience: false,
	inference: false,
	runtime: true,
};

/** Human label for a runtime provider kind. */
export function sandboxProviderLabel(kind: string): string {
	if (kind === "vercel") return "Vercel";
	return kind.charAt(0).toUpperCase() + kind.slice(1);
}

/**
 * Domain to pull the provider's favicon from, so a runtime row carries its
 * real logo like every other connector instead of a letter-mark. Unknown kinds
 * return null and `ConnectorMark` degrades to the monogram on its own.
 */
export function sandboxProviderFaviconDomain(kind: string): string | null {
	if (kind === "vercel") return "vercel.com";
	return null;
}

export function parseSandboxRowKey(connectorKey: string): string | null {
	if (!connectorKey.startsWith(SANDBOX_ROW_KEY_PREFIX)) return null;
	const id = connectorKey.slice(SANDBOX_ROW_KEY_PREFIX.length);
	return id.length > 0 ? id : null;
}

export function parseSandboxProviderKey(connectorKey: string): string | null {
	if (!connectorKey.startsWith(SANDBOX_PROVIDER_KEY_PREFIX)) return null;
	const kind = connectorKey.slice(SANDBOX_PROVIDER_KEY_PREFIX.length);
	return kind.length > 0 ? kind : null;
}

/** True when the key is either a sandbox instance or a sandbox provider kind. */
export function isSandboxConnectorKey(connectorKey: string): boolean {
	return (
		parseSandboxRowKey(connectorKey) !== null ||
		parseSandboxProviderKey(connectorKey) !== null
	);
}

interface SandboxConnectorRow {
	connector: ConnectorDefinitionItem;
	facets: ConnectionFacets;
	sandbox: SandboxItem;
}

/**
 * Org sandbox instances as list rows. Built-in local runtime is omitted (not
 * a row you can disconnect).
 */
export function buildSandboxConnectorRows(
	sandboxes: readonly SandboxItem[],
): SandboxConnectorRow[] {
	return sandboxes.map((sandbox) => ({
		sandbox,
		facets: RUNTIME_ONLY_FACETS,
		connector: {
			key: `${SANDBOX_ROW_KEY_PREFIX}${sandbox.id}`,
			name: sandbox.name || sandboxProviderLabel(sandbox.providerKind),
			description: null,
			version: "",
			auth_schema: null,
			feeds_schema: null,
			actions_schema: null,
			options_schema: null,
			status: sandbox.connected ? "active" : "error",
			login_enabled: false,
			installed: true,
		} as ConnectorDefinitionItem,
	}));
}

/**
 * Catalog entries for each connectable runtime provider kind. These are the
 * "kinds" you pick under Add connector — same place as Slack/GitHub, filtered
 * by the Runtime chip. Selecting one routes to the locked create form.
 */
export function buildSandboxProviderCatalogEntries(
	availableProviders: readonly string[],
): ConnectorDefinitionItem[] {
	return availableProviders.map(
		(kind) =>
			({
				key: `${SANDBOX_PROVIDER_KEY_PREFIX}${kind}`,
				name: sandboxProviderLabel(kind),
				description: `Run agents in a ${sandboxProviderLabel(kind)} sandbox instead of the built-in local runtime.`,
				version: "",
				auth_schema: null,
				feeds_schema: null,
				actions_schema: null,
				options_schema: null,
				status: "active",
				login_enabled: false,
				favicon_domain: sandboxProviderFaviconDomain(kind),
				// Catalog entry, not yet an org sandbox instance.
				installed: false,
				installable: false,
			}) as ConnectorDefinitionItem,
	);
}

export function sandboxCatalogFacets(): ConnectionFacets {
	return RUNTIME_ONLY_FACETS;
}
